import React, { useEffect, useRef, useState } from "react";
import getScrollableParent from "./scrollable-parent";
import ArrowCanvas from "./arrow-canvas";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import Slider from "@material-ui/core/Slider";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import FullscreenExitIcon from "@material-ui/icons/FullscreenExit";

const NODES_VERTICAL_SPACE = 40;
const NODES_HORIZONTAL_SPACE = 40;
const MIN_ZOOM = 25;
const MAX_ZOOM = 200;
const ZOOM_STEP = 10;

function transformGraphData(graphData, onResourceClick) {
  const nodes = Object.values(graphData).map((contract) => {
    const content = (
      <div
        style={{
          padding: '12px',
          border: '1px solid #bdbdbd',
          borderRadius: '8px',
          backgroundColor: '#f7fafc',
          minWidth: 180,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <strong style={{ fontSize: '1.1em', color: '#222' }}>{contract.label}</strong>
        {contract.functions && (
          <div style={{ marginTop: '10px' }}>
            <strong style={{ color: '#333', fontSize: 13 }}>Functions:</strong>
            {Object.entries(contract.functions).map(([visibility, funcs]) => (
              funcs.length > 0 && (
                <div key={`func-${visibility}`} style={{ marginLeft: '10px' }}>
                  <strong style={{ color: getColorForVisibility(visibility), fontSize: 12 }}>{capitalize(visibility)}:</strong>
                  <ul style={{ paddingLeft: 16, margin: 0 }}>
                    {funcs.map((func, index) => (
                      <li
                        key={`func-${visibility}-${index}`}
                        style={{
                          color: getColorForVisibility(visibility),
                          cursor: 'pointer',
                          textDecoration: visibility === 'external' ? 'line-through' : 'none',
                          fontWeight: func.overloading && func.overloading.length > 0 ? 'bold' : 'normal',
                          fontSize: 13,
                          marginBottom: 2,
                          transition: "background 0.15s"
                        }}
                        onClick={() => onResourceClick(func)}
                        onMouseEnter={e => e.currentTarget.style.textDecoration = visibility === 'external' ? 'line-through underline' : 'underline'}
                        onMouseLeave={e => e.currentTarget.style.textDecoration = visibility === 'external' ? 'line-through' : 'none'}
                      >
                        {func.overLoadedBy ? func.name : func.name} <span style={{ fontSize: 11, color: "#888" }}>({func.stateMutability})</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            ))}
          </div>
        )}
        {contract.modifiers?.length > 0 && (
          <div style={{ marginTop: '10px' }}>
            <strong style={{ color: '#333', fontSize: 13 }}>Modifiers:</strong>
            <ul style={{ paddingLeft: 16, margin: 0 }}>
              {contract.modifiers.map((modifier, index) => (
                <li
                  key={`modifier-${index}`}
                  style={{
                    color: '#ffc107',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    fontSize: 13,
                    marginBottom: 2,
                  }}
                  onClick={() => onResourceClick(modifier)}
                  onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                  onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                >
                  {modifier.name}
                </li>
              ))}
            </ul>
          </div>
        )}
        {contract.events?.length > 0 && (
          <div style={{ marginTop: '10px' }}>
            <strong style={{ color: '#333', fontSize: 13 }}>Events:</strong>
            <ul style={{ paddingLeft: 16, margin: 0 }}>
              {contract.events.map((event, index) => (
                <li
                  key={`event-${index}`}
                  style={{
                    color: '#17a2b8',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    fontWeight: event.overloading && event.overloading.length > 0 ? 'bold' : 'normal',
                    fontSize: 13,
                    marginBottom: 2,
                  }}
                  onClick={() => onResourceClick(event)}
                  onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                  onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                >
                  {event.overLoadedBy ? `-- ${event.name} --` : event.name}
                </li>
              ))}
            </ul>
          </div>
        )}
        {contract.storage && (
          <div style={{ marginTop: '10px' }}>
            <strong style={{ color: '#333', fontSize: 13 }}>Storage:</strong>
            {Object.entries(contract.storage).map(([visibility, stores]) => (
              stores.length > 0 && (
                <div key={`storage-${visibility}`} style={{ marginLeft: '10px' }}>
                  <strong style={{ color: getColorForVisibility(visibility), fontSize: 12 }}>{capitalize(visibility)}:</strong>
                  <ul style={{ paddingLeft: 16, margin: 0 }}>
                    {stores.map((store, index) => (
                      <li
                        key={`storage-${visibility}-${index}`}
                        style={{
                          color: getColorForVisibility(visibility),
                          cursor: 'pointer',
                          textDecoration: visibility === 'external' ? 'line-through' : 'none',
                          fontSize: 13,
                          marginBottom: 2,
                        }}
                        onClick={() => onResourceClick(store)}
                        onMouseEnter={e => e.currentTarget.style.textDecoration = visibility === 'external' ? 'line-through underline' : 'underline'}
                        onMouseLeave={e => e.currentTarget.style.textDecoration = visibility === 'external' ? 'line-through' : 'none'}
                      >
                        {store.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )
            ))}
          </div>
        )}
      </div>
    );

    return {
      id: contract.id,
      content,
      label: contract.label,
      childs: contract.dependencies?.map((dep) => dep.name) || [],
      root: false,
    };
  });

  // Identify root nodes (contracts with no dependencies)
  const rootNodes = nodes.filter(
    (node) => !Object.values(graphData).some((contract) =>
      contract.dependencies.some((dep) => dep.name === node.id)
    )
  );

  rootNodes.forEach((node) => (node.root = true));
  return nodes;
}

function getColorForVisibility(visibility) {
  switch (visibility) {
    case 'public':
      return '#007bff';
    case 'private':
      return '#dc3545';
    case 'internal':
      return '#6c757d';
    case 'external':
      return '#28a745';
    default:
      return '#333';
  }
}

function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export default function GraphViewer({ graphData, onResourceClick }) {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const nodes = transformGraphData(graphData, onResourceClick);
  const [openedChilds, set_openedChilds] = useState({});
  const [rootSizes, set_rootSizes] = useState({});

  const rootNodes = nodes.filter((node) => node.root);
  const rootTops = {};
  let nextRootTop = 0;
  rootNodes.forEach((rootNode) => {
    rootTops[rootNode.id] = nextRootTop;
    nextRootTop += (rootSizes[rootNode.id]?.height || 0) + NODES_VERTICAL_SPACE;
  });

  const expandAll = () => {
    const allOpen = {};
    const openAllNodes = (node, path) => {
      allOpen[path] = true;
      node.childs.forEach(childId => {
        const childNode = nodes.find(n => n.id === childId);
        if (childNode) {
          openAllNodes(childNode, `${path}/${childNode.id}`);
        }
      });
    };
    nodes.forEach(node => {
      if (node.root) {
        openAllNodes(node, `/${node.id}`);
      }
    });
    set_openedChilds(allOpen);
  };

  const collapseAll = () => set_openedChilds({});

  const [isFullScreen, set_isFullScreen] = useState(false);

  useEffect(() => {
    if (!isFullScreen) return undefined;
    const onKeyDown = (e) => e.key === "Escape" && set_isFullScreen(false);
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isFullScreen]);

  const panRef = useRef(null);
  const [zoom, set_zoom] = useState(100);
  const changeZoom = (value) =>
    set_zoom(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value)));
  const dragState = useRef({ active: false, moved: false });
  const [isPanning, set_isPanning] = useState(false);

  const startPan = (e) => {
    if (e.button !== 0 || !panRef.current) return;
    const el = panRef.current;
    const startX = e.clientX;
    const startY = e.clientY;
    const startLeft = el.scrollLeft;
    const startTop = el.scrollTop;
    dragState.current = { active: true, moved: false };

    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (!dragState.current.moved && Math.hypot(dx, dy) < 4) return;
      if (!dragState.current.moved) set_isPanning(true);
      dragState.current.moved = true;
      el.scrollLeft = startLeft - dx;
      el.scrollTop = startTop - dy;
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      dragState.current.active = false;
      set_isPanning(false);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // Swallow the click that ends a drag so it doesn't trigger node actions
  const suppressClickAfterPan = (e) => {
    if (dragState.current.moved) {
      e.stopPropagation();
      e.preventDefault();
      dragState.current.moved = false;
    }
  };

  // Responsive: horizontal scroll for overflow, padding for mobile/tablet
  return (
    <div
      style={{
        position: isFullScreen ? "fixed" : "relative",
        ...(isFullScreen && { top: 0, left: 0, zIndex: 1400 }),
        padding: isMobile ? 0 : isTablet ? 4 : 0,
        overflowX: "auto",
        minHeight: isMobile ? 600 : 400,
        height: isFullScreen ? "100vh" : "100%",
        width: isFullScreen ? "100vw" : "100%",
        maxWidth: "100vw",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#fff"
      }}
    >
      <div
        style={{
          position: "absolute",
          top: isMobile ? 4 : 10,
          right: isMobile ? 4 : 16,
          zIndex: 1, // Reduced z-index so it doesn't overlap headers/content
          display: "flex",
          gap: 8,
        }}
      >
        <Tooltip
          title={isFullScreen ? "Back to original screen" : "Full screen"}
          PopperProps={{ style: { zIndex: 1500 } }}
        >
          <button
            style={{
              cursor: "pointer",
              background: "#fff",
              border: "1px solid #bbb",
              borderRadius: 6,
              padding: isMobile ? "2px 6px" : "4px 8px",
              display: "flex",
              alignItems: "center",
              fontWeight: 600,
              fontSize: isMobile ? 12 : 14,
              marginRight: 4,
              boxShadow: isMobile ? "none" : "0 1px 2px rgba(0,0,0,0.04)",
            }}
            aria-label={isFullScreen ? "Back to original screen" : "Full screen"}
            onClick={() => set_isFullScreen((prev) => !prev)}
          >
            {isFullScreen ? (
              <FullscreenExitIcon fontSize="small" />
            ) : (
              <FullscreenIcon fontSize="small" />
            )}
          </button>
        </Tooltip>
        <button
          style={{
            cursor: "pointer",
            background: "#fff",
            border: "1px solid #bbb",
            borderRadius: 6,
            padding: isMobile ? "2px 6px" : "4px 10px",
            fontWeight: 600,
            fontSize: isMobile ? 12 : 14,
            marginRight: 4,
            boxShadow: isMobile ? "none" : "0 1px 2px rgba(0,0,0,0.04)",
          }}
          title="Expand All"
          onClick={expandAll}
        >
          Expand All
        </button>
        <button
          style={{
            cursor: "pointer",
            background: "#fff",
            border: "1px solid #bbb",
            borderRadius: 6,
            padding: isMobile ? "2px 6px" : "4px 10px",
            fontWeight: 600,
            fontSize: isMobile ? 12 : 14,
            boxShadow: isMobile ? "none" : "0 1px 2px rgba(0,0,0,0.04)",
          }}
          title="Collapse All"
          onClick={collapseAll}
        >
          Collapse All
        </button>
      </div>
      <div
        ref={panRef}
        onMouseDown={startPan}
        onClickCapture={suppressClickAfterPan}
        style={{
          position: "relative",
          cursor: isPanning ? "grabbing" : "grab",
          userSelect: isPanning ? "none" : undefined,
          minWidth: isMobile ? 180 : 320,
          minHeight: isMobile ? 120 : 200,
          overflow: "auto",
          paddingBottom: isMobile ? 8 : 24,
          flex: 1,
          height: "100%",
        }}
      >
        <div
          style={{
            position: "relative",
            transform: `scale(${zoom / 100})`,
            transformOrigin: "0 0",
          }}
        >
          {rootNodes.map((rootNode) => (
            <Node
              key={rootNode.id}
              top={rootTops[rootNode.id]}
              set_openedChilds={set_openedChilds}
              left={0}
              openedChilds={openedChilds}
              node={rootNode}
              nodes={nodes}
              path={`/${rootNode.id}`}
              updateSize={(size) =>
                set_rootSizes((prev) => ({ ...prev, [rootNode.id]: size }))
              }
              onResourceClick={onResourceClick}
            />
          ))}
        </div>
      </div>
      <div
        style={{
          position: "absolute",
          bottom: isMobile ? 4 : 12,
          right: isMobile ? 4 : 16,
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "2px 10px",
          background: "#fff",
          border: "1px solid #bbb",
          borderRadius: 6,
          boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
        }}
      >
        <button
          style={zoomButtonStyle}
          aria-label="Zoom out"
          title="Zoom out"
          disabled={zoom <= MIN_ZOOM}
          onClick={() => changeZoom(zoom - ZOOM_STEP)}
        >
          <RemoveIcon fontSize="small" />
        </button>
        <Slider
          value={zoom}
          min={MIN_ZOOM}
          max={MAX_ZOOM}
          step={5}
          onChange={(_, value) => changeZoom(value)}
          aria-label="Zoom"
          style={{ width: isMobile ? 90 : 160 }}
        />
        <button
          style={zoomButtonStyle}
          aria-label="Zoom in"
          title="Zoom in"
          disabled={zoom >= MAX_ZOOM}
          onClick={() => changeZoom(zoom + ZOOM_STEP)}
        >
          <AddIcon fontSize="small" />
        </button>
        <span style={{ minWidth: 40, textAlign: "right", fontSize: 13 }}>
          {zoom}%
        </span>
      </div>
    </div>
  );
}

const zoomButtonStyle = {
  cursor: "pointer",
  background: "transparent",
  border: "none",
  display: "flex",
  alignItems: "center",
  padding: 2,
};

function Node({
  node,
  nodes,
  updateSize = null,
  top,
  left,
  openedChilds,
  set_openedChilds,
  path,
  onResourceClick,
}) {
  if (!node.childs) node.childs = [];
  let childNodes = nodes.filter((x) => node.childs.includes(x.id));
  const hasChilds = childNodes.length > 0;
  if (!openedChilds[path]) childNodes = [];

  const [childNodeSizes, set_childNodeSizes] = useState({});
  const [lastSizeCache, set_lastSizeCache] = useState({ width: 0, height: 0 });

  function childSizeChanged(nodeId, size) {
    set_childNodeSizes((prev) => ({
      ...prev,
      [nodeId]: size,
    }));
  }

  const nodeElement = useRef(null);
  const contentElement = useRef(null);

  const [childNodesPositions, set_childNodePosition] = useState({});
  const [contentTop, set_contentTop] = useState(0);
  const [isReady, set_isReady] = useState(false);

  useEffect(() => {
    if (!nodeElement.current) return;
    const childSizesReady = childNodes.every((c) => childNodeSizes[c.id]);
    if (childSizesReady) {
      const totalChildHeight =
        childNodes.reduce(
          (prev, node) => prev + (childNodeSizes[node.id]?.height || 0),
          0
        ) + Math.max(0, childNodes.length - 1) * NODES_VERTICAL_SPACE;
      const meWidth = nodeElement.current.offsetWidth;
      const meHeight = Math.max(
        nodeElement.current.offsetHeight,
        totalChildHeight
      );
      const meContentHeight = contentElement.current.offsetHeight;

      let newContentTop = totalChildHeight > meContentHeight
        ? totalChildHeight / 2 - meContentHeight / 2
        : 0;
      set_contentTop((prev) => {
        if (node.root && prev - newContentTop !== 0) {
          const scrollElement = getScrollableParent(contentElement.current);
          if (scrollElement)
            scrollElement.scrollTop = scrollElement.scrollTop - (prev - newContentTop);
        }
        return newContentTop;
      });

      const currentLeft = meWidth + NODES_HORIZONTAL_SPACE;
      let currentTop = 0;
      const newChildNodesPositions = {};
      for (let i = 0; i < childNodes.length; i++) {
        const childNode = childNodes[i];
        newChildNodesPositions[childNode.id] = {
          top: currentTop,
          left: currentLeft,
        };
        currentTop += (childNodeSizes[childNode.id]?.height || 0) + NODES_VERTICAL_SPACE;
      }

      if (
        JSON.stringify(newChildNodesPositions) !==
        JSON.stringify(childNodesPositions)
      ) {
        set_childNodePosition(newChildNodesPositions);
      }

      if (
        meWidth !== 0 &&
        meHeight !== 0 &&
        (meWidth !== lastSizeCache.width || meHeight !== lastSizeCache.height)
      ) {
        updateSize?.({ width: meWidth, height: meHeight });
        set_lastSizeCache({
          width: meWidth,
          height: meHeight,
        });
        set_isReady(true);
      }
    }
    // eslint-disable-next-line
  });

  function toggle() {
    set_openedChilds((prev) => {
      const updated = { ...prev };
      if (!updated[path]) {
        updated[path] = true;
      } else {
        Object.keys(updated).forEach((x) => {
          if (x.startsWith(path)) delete updated[x];
        });
      }
      return updated;
    });
  }

  // Helper to only set valid numeric style values
  const safeStyle = (value) => (typeof value === "number" && !isNaN(value) ? value : undefined);

  return (
    <div
      ref={nodeElement}
      style={{
        position: "absolute",
        visibility: isReady ? "visible" : "hidden",
        top: safeStyle(top),
        left: safeStyle(left),
        display: "inline-block",
        minWidth: 180,
        maxWidth: "90vw",
      }}
    >
      <div
        ref={contentElement}
        style={{
          whiteSpace: "normal",
          position: "relative",
          border: "1px solid #333",
          borderRadius: 8,
          background: "#fff",
          padding: 12,
          top: contentTop,
          userSelect: "none",
          minWidth: 180,
          maxWidth: 320,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        }}
      >
        {node.content}
      </div>
      {hasChilds && (
        <svg
          onClick={toggle}
          style={{
            cursor: "pointer",
            position: "absolute",
            zIndex: 1,
            left: safeStyle(contentElement.current?.offsetWidth + 5 - 5),
            top: safeStyle(contentTop + (contentElement.current?.offsetHeight || 0) / 2 - 8),
          }}
          width="18"
          height="18"
          viewBox="0 0 36 36"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="18"
            cy="18"
            r="16"
            stroke="black"
            strokeWidth="4"
            fill="white"
          />
        </svg>
      )}
      {childNodes.map((childNode) => (
        <React.Fragment key={childNode.id}>
          <ArrowCanvas
            fromX={safeStyle(contentElement.current?.offsetWidth + 5)}
            fromY={safeStyle(contentTop + (contentElement.current?.offsetHeight || 0) / 2)}
            toX={safeStyle(childNodesPositions[childNode.id]?.left - 5)}
            toY={safeStyle(
              childNodesPositions[childNode.id]?.top +
                (childNodeSizes[childNode.id]?.height || 0) / 2
            )}
          />
          <Node
            path={path + "/" + childNode.id}
            set_openedChilds={set_openedChilds}
            openedChilds={openedChilds}
            top={childNodesPositions[childNode.id]?.top}
            left={childNodesPositions[childNode.id]?.left}
            node={childNode}
            nodes={nodes}
            updateSize={(size) => childSizeChanged(childNode.id, size)}
            onResourceClick={onResourceClick}
          />
        </React.Fragment>
      ))}
    </div>
  );
}
